import { useCallback, useMemo, useState } from 'react';

import { CitationParagraph } from '../FileParagraphPicker';

export function normalizeSearchText(text: string) {
  return text.normalize('NFKC').toLowerCase().replace(/\s+/gu, ' ').trim();
}

/** Finds literal text within paragraphs. Each paragraph is one result. */
export function useParagraphFind(paragraphs: CitationParagraph[]) {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  // Increments on each Next or Previous. The viewer scrolls to the selected
  // match on each move, also when the selection does not change.
  const [moveCount, setMoveCount] = useState(0);
  const indexed = useMemo(
    () =>
      [...paragraphs]
        .filter((paragraph) =>
          paragraph.boundingRegions.some((region) => region.Polygon.length >= 3)
        )
        .sort(
          (a, b) =>
            a.impliedParagraphNumber - b.impliedParagraphNumber ||
            a.id.localeCompare(b.id)
        )
        .map((paragraph) => ({
          paragraph,
          text: normalizeSearchText(paragraph.content),
        })),
    [paragraphs]
  );
  const normalizedQuery = normalizeSearchText(query);
  const matches = useMemo(
    () =>
      normalizedQuery
        ? indexed
            .filter(({ text }) => text.includes(normalizedQuery))
            .map(({ paragraph }) => paragraph)
        : [],
    [indexed, normalizedQuery]
  );
  const selected = matches[index] ?? matches[0];
  const selectedIndex = selected ? matches.indexOf(selected) : 0;
  const updateQuery = useCallback((value: string) => {
    setQuery(value);
    setIndex(0);
  }, []);

  return {
    query,
    matches,
    selected,
    index: selectedIndex,
    moveCount,
    setQuery: updateQuery,
    move: (direction: number) => {
      if (matches.length) {
        setIndex((selectedIndex + direction + matches.length) % matches.length);
        setMoveCount((count) => count + 1);
      }
    },
  };
}
